import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Download,
  Store as StoreIcon,
  Person as PersonIcon,
  ExpandMore,
  Search,
  FilterList,
  Assessment
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { AppHeader } from '../components/AppHeader';
import api from '../services/api';
import { campaignService } from '../services/campaignService';

interface Campaign {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status?: string;
}

interface CouponsByStore {
  storeId: string;
  storeName: string;
  campaignName: string;
  totalCoupons: number;
  coupons: {
    id: string;
    couponNumber: number;
    couponCode: string;
    userName: string;
    userEmail: string;
    sellerName?: string;
    createdAt: string;
  }[];
}

interface CouponsByUser {
  userId: string;
  userName: string;
  userEmail: string;
  userCpf: string;
  totalCoupons: number;
  coupons: {
    id: string;
    couponNumber: number;
    couponCode: string;
    campaignName: string;
    storeName: string;
    sellerName?: string;
    createdAt: string;
  }[];
}

interface ReportSummary {
  totalCoupons: number;
  totalUsers: number;
  totalStores: number;
  totalCampaigns: number;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

  const Reports: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  
  const [activeTab, setActiveTab] = useState(0);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [storeReports, setStoreReports] = useState<CouponsByStore[]>([]);
  const [userReports, setUserReports] = useState<CouponsByUser[]>([]);
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Filtros
  const [filters, setFilters] = useState({
    campaignId: '',
    startDate: '',
    endDate: '',
    search: '',
  });

  // Verificar se é ASSOCIACAO
  React.useEffect(() => {
    if (!user || user.role !== 'ASSOCIACAO') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Carregar campanhas
  const loadCampaigns = useCallback(async () => {
    try {
      const data = await campaignService.getCampaignsByAssociation();
      setCampaigns(data);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
      showError('Erro ao carregar campanhas');
    }
  }, [showError]);

  const loadStoreReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.campaignId) params.append('campaignId', filters.campaignId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/reports/coupons/by-store?${params.toString()}`);
      setStoreReports(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatório por loja:', error);
      showError('Erro ao carregar relatório por loja');
    } finally {
      setLoading(false);
    }
  }, [filters, showError]);

  const loadUserReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.campaignId) params.append('campaignId', filters.campaignId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/reports/coupons/by-user?${params.toString()}`);
      setUserReports(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatório por usuário:', error);
      showError('Erro ao carregar relatório por usuário');
    } finally {
      setLoading(false);
    }
  }, [filters, showError]);

  const loadSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      
      if (filters.campaignId) params.append('campaignId', filters.campaignId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await api.get(`/reports/summary?${params.toString()}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Erro ao carregar resumo:', error);
    }
  }, [filters]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  // Carregar dados quando filtros mudam
  useEffect(() => {
    if (activeTab === 0) {
      loadStoreReports();
    } else if (activeTab === 1) {
      loadUserReports();
    }
    loadSummary();
  }, [filters, activeTab, loadStoreReports, loadUserReports, loadSummary]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      campaignId: '',
      startDate: '',
      endDate: '',
      search: '',
    });
  };

  const exportToCSV = (data: any[], filename: string) => {
    try {
      let csvContent = '';
      
      if (activeTab === 0 && storeReports.length > 0) {
        // Relatório por loja
        csvContent = 'Loja,Campanha,Total Cupons,Número Cupom,Código Cupom,Usuário,Email,Vendedor,Data\n';
        storeReports.forEach(store => {
          store.coupons.forEach(coupon => {
            csvContent += `"${store.storeName}","${store.campaignName}",${store.totalCoupons},${coupon.couponNumber},"${coupon.couponCode}","${coupon.userName}","${coupon.userEmail}","${coupon.sellerName || 'N/A'}","${new Date(coupon.createdAt).toLocaleDateString('pt-BR')}"\n`;
          });
        });
      } else if (activeTab === 1 && userReports.length > 0) {
        // Relatório por usuário
        csvContent = 'Usuário,Email,CPF,Total Cupons,Número Cupom,Código Cupom,Campanha,Loja,Vendedor,Data\n';
        userReports.forEach(user => {
          user.coupons.forEach(coupon => {
            csvContent += `"${user.userName}","${user.userEmail}","${user.userCpf}",${user.totalCoupons},${coupon.couponNumber},"${coupon.couponCode}","${coupon.campaignName}","${coupon.storeName}","${coupon.sellerName || 'N/A'}","${new Date(coupon.createdAt).toLocaleDateString('pt-BR')}"\n`;
          });
        });
      }
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSuccess('Relatório exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      showError('Erro ao exportar relatório');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <>
      <AppHeader 
        title="Relatórios de Cupons" 
        subtitle="Análise detalhada de cupons por loja e usuário"
        icon={<Assessment />}
      />

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Resumo */}
        {summary && (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, 
            gap: 3, 
            mb: 4 
          }}>
            <Card elevation={2} sx={{ borderLeft: '4px solid #1976d2' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2" sx={{ fontWeight: 500 }}>
                      Total de Cupons
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 600 }}>
                      {summary.totalCoupons.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ fontSize: '2rem', color: '#1976d2', opacity: 0.7 }}>
                    🎫
                  </Box>
                </Box>
              </CardContent>
            </Card>
            
            <Card elevation={2} sx={{ borderLeft: '4px solid #9c27b0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2" sx={{ fontWeight: 500 }}>
                      Usuários Únicos
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#9c27b0', fontWeight: 600 }}>
                      {summary.totalUsers.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ fontSize: '2rem', color: '#9c27b0', opacity: 0.7 }}>
                    👥
                  </Box>
                </Box>
              </CardContent>
            </Card>
            
            <Card elevation={2} sx={{ borderLeft: '4px solid #388e3c' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2" sx={{ fontWeight: 500 }}>
                      Lojas Participantes
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#388e3c', fontWeight: 600 }}>
                      {summary.totalStores.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ fontSize: '2rem', color: '#388e3c', opacity: 0.7 }}>
                    🏪
                  </Box>
                </Box>
              </CardContent>
            </Card>
            
            <Card elevation={2} sx={{ borderLeft: '4px solid #f57c00' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2" sx={{ fontWeight: 500 }}>
                      Campanhas Ativas
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#f57c00', fontWeight: 600 }}>
                      {summary.totalCampaigns.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ fontSize: '2rem', color: '#f57c00', opacity: 0.7 }}>
                    📢
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          {/* Filtros */}
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: 'primary.main',
                fontWeight: 600
              }}
            >
              <FilterList sx={{ mr: 1 }} />
              Filtros de Busca
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 2fr 1fr' }, 
              gap: 2,
              mt: 2
            }}>
              <FormControl fullWidth size="small">
                <InputLabel>Campanha</InputLabel>
                <Select
                  value={filters.campaignId}
                  label="Campanha"
                  onChange={(e) => handleFilterChange('campaignId', e.target.value)}
                >
                  <MenuItem value="">Todas as campanhas</MenuItem>
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Data Inicial"
                type="date"
                size="small"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                fullWidth
                label="Data Final"
                type="date"
                size="small"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                fullWidth
                label={activeTab === 0 ? "Pesquisar loja" : "Pesquisar usuário"}
                size="small"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
              
              <Button
                variant="outlined"
                onClick={clearFilters}
                fullWidth
                size="small"
                sx={{ height: 'fit-content' }}
              >
                🧹 Limpar
              </Button>
            </Box>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem'
                }
              }}
            >
              <Tab 
                icon={<StoreIcon />} 
                iconPosition="start"
                label="Relatório por Loja" 
              />
              <Tab 
                icon={<PersonIcon />} 
                iconPosition="start"
                label="Relatório por Usuário" 
              />
            </Tabs>
          </Box>

          {/* Botão de Exportar */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => exportToCSV(
                activeTab === 0 ? storeReports : userReports, 
                activeTab === 0 ? 'relatorio-por-loja' : 'relatorio-por-usuario'
              )}
              disabled={loading || (activeTab === 0 ? storeReports.length === 0 : userReports.length === 0)}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Exportar CSV
            </Button>
          </Box>

          {/* Conteúdo das Tabs */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Tab 0: Relatório por Loja */}
              {activeTab === 0 && (
                <Box>
                  {storeReports.length === 0 ? (
                    <Alert severity="info">
                      Nenhum dado encontrado para os filtros selecionados.
                    </Alert>
                  ) : (
                    storeReports.map((store) => (
                      <Accordion key={store.storeId} sx={{ mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <StoreIcon sx={{ mr: 2, color: 'primary.main' }} />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="h6">{store.storeName}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {store.campaignName}
                              </Typography>
                            </Box>
                            <Chip 
                              label={`${store.totalCoupons} cupons`} 
                              color="primary" 
                              size="small" 
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Número</TableCell>
                                  <TableCell>Código</TableCell>
                                  <TableCell>Usuário</TableCell>
                                  <TableCell>Email</TableCell>
                                  <TableCell>Vendedor</TableCell>
                                  <TableCell>Data</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {store.coupons.map((coupon) => (
                                  <TableRow key={coupon.id}>
                                    <TableCell>{coupon.couponNumber}</TableCell>
                                    <TableCell>
                                      <Typography variant="body2" fontFamily="monospace">
                                        {coupon.couponCode}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>{coupon.userName}</TableCell>
                                    <TableCell>{coupon.userEmail}</TableCell>
                                    <TableCell>{coupon.sellerName || 'N/A'}</TableCell>
                                    <TableCell>{formatDate(coupon.createdAt)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    ))
                  )}
                </Box>
              )}

              {/* Tab 1: Relatório por Usuário */}
              {activeTab === 1 && (
                <Box>
                  {userReports.length === 0 ? (
                    <Alert severity="info">
                      Nenhum dado encontrado para os filtros selecionados.
                    </Alert>
                  ) : (
                    userReports.map((user) => (
                      <Accordion key={user.userId} sx={{ mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <PersonIcon sx={{ mr: 2, color: 'secondary.main' }} />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="h6">{user.userName}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {user.userEmail} • CPF: {user.userCpf}
                              </Typography>
                            </Box>
                            <Chip 
                              label={`${user.totalCoupons} cupons`} 
                              color="secondary" 
                              size="small" 
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Número</TableCell>
                                  <TableCell>Código</TableCell>
                                  <TableCell>Campanha</TableCell>
                                  <TableCell>Loja</TableCell>
                                  <TableCell>Vendedor</TableCell>
                                  <TableCell>Data</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {user.coupons.map((coupon) => (
                                  <TableRow key={coupon.id}>
                                    <TableCell>{coupon.couponNumber}</TableCell>
                                    <TableCell>
                                      <Typography variant="body2" fontFamily="monospace">
                                        {coupon.couponCode}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>{coupon.campaignName}</TableCell>
                                    <TableCell>{coupon.storeName}</TableCell>
                                    <TableCell>{coupon.sellerName || 'N/A'}</TableCell>
                                    <TableCell>{formatDate(coupon.createdAt)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    ))
                  )}
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </>
  );
}; 
export default Reports;